// VendorManagement
import React, { useState, useEffect } from 'react';
import {
    Building, User, Mail, Phone, Globe, MapPin, CreditCard, FileText,
    Save, X, Plus, AlertCircle, CheckCircle, Loader, Hash, Building2,
    Briefcase, Shield, Calculator, Receipt, IndianRupee, Copy, Check,
    AlertTriangle, Info, Upload, Trash2, Edit2, Search, Filter
} from 'lucide-react';

const API_BASE_URL = 'https://api.zapbooksai.subraps.com/api';

const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    return {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
    };
};

// Vendor Creation Modal Component
const VendorCreationModal = ({ isOpen, onClose, onSuccess, existingVendor = null, currentOrganization }) => {
    const [formData, setFormData] = useState({
        // Basic Information
        contact_name: '',
        company_name: '',
        contact_type: 'vendor',

        // Contact Details
        email: '',
        phone: '',
        mobile: '',
        website: '',

        // GST Details
        gst_no: '',
        gst_treatment: 'business_gst',
        place_of_supply: '',

        // Tax Details
        tax_id: '',
        tax_name: '',
        tax_percentage: '',
        is_tds_applicable: false,
        tds_tax_id: '',
        tax_authority_id: '',
        tax_exemption_id: '',

        // Payment Terms
        payment_terms: 0,
        payment_terms_label: 'Due on Receipt',
        currency_code: 'INR',

        // Billing Address
        billing_attention: '',
        billing_address: '',
        billing_street2: '',
        billing_city: '',
        billing_state: '',
        billing_zip: '',
        billing_country: 'India',
        billing_fax: '',

        // Shipping Address
        shipping_attention: '',
        shipping_address: '',
        shipping_street2: '',
        shipping_city: '',
        shipping_state: '',
        shipping_zip: '',
        shipping_country: 'India',
        shipping_fax: '',

        // Additional Details
        contact_persons: [],
        notes: '',
        custom_fields: []
    });

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [activeTab, setActiveTab] = useState('basic');
    const [gstValidation, setGstValidation] = useState({ isValid: false, message: '' });
    const [sameAsShipping, setSameAsShipping] = useState(true);
    const [availableTaxes, setAvailableTaxes] = useState([]);
    const [tdsTaxes, setTdsTaxes] = useState([]);
    const [searchingGST, setSearchingGST] = useState(false);
    const [taxesLoading, setTaxesLoading] = useState(false);

    // Indian States for GST
    const indianStates = [
        'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
        'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
        'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
        'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
        'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
        'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
        'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
    ];

    // GST Treatment Options
    const gstTreatmentOptions = [
        { value: 'business_gst', label: 'Registered Business - Regular' },
        { value: 'business_composition', label: 'Registered Business - Composition' },
        { value: 'overseas', label: 'Overseas' },
        { value: 'consumer', label: 'Consumer' },
        { value: 'business_none', label: 'Unregistered Business' },
        { value: 'special_economic_zone', label: 'Special Economic Zone' },
        { value: 'deemed_export', label: 'Deemed Export' }
    ];

    // Payment Terms Options
    const paymentTermsOptions = [
        { value: 0, label: 'Due on Receipt' },
        { value: 15, label: 'Net 15' },
        { value: 30, label: 'Net 30' },
        { value: 45, label: 'Net 45' },
        { value: 60, label: 'Net 60' },
        { value: 90, label: 'Net 90' },
        { value: -1, label: 'Due end of the month' },
        { value: -2, label: 'Due end of next month' }
    ];

    // Bank Account Types
    const bankAccountTypes = [
        { value: 'savings', label: 'Savings' },
        { value: 'current', label: 'Current' },
        { value: 'overdraft', label: 'Overdraft' },
        { value: 'cash_credit', label: 'Cash Credit' }
    ];

    // Default tax options (fallback when API fails)
    const defaultTaxOptions = [
        { tax_id: 'gst_0', tax_name: 'GST 0%', tax_percentage: 0 },
        { tax_id: 'gst_5', tax_name: 'GST 5%', tax_percentage: 5 },
        { tax_id: 'gst_12', tax_name: 'GST 12%', tax_percentage: 12 },
        { tax_id: 'gst_18', tax_name: 'GST 18%', tax_percentage: 18 },
        { tax_id: 'gst_28', tax_name: 'GST 28%', tax_percentage: 28 }
    ];

    const defaultTdsOptions = [
        { tax_id: 'tds_1', tax_name: 'TDS 1%', tax_percentage: 1 },
        { tax_id: 'tds_2', tax_name: 'TDS 2%', tax_percentage: 2 },
        { tax_id: 'tds_5', tax_name: 'TDS 5%', tax_percentage: 5 },
        { tax_id: 'tds_10', tax_name: 'TDS 10%', tax_percentage: 10 }
    ];

    // Load existing vendor data if editing
    useEffect(() => {
        if (existingVendor) {
            setFormData({
                ...formData,
                ...existingVendor,
                contact_persons: existingVendor.contact_persons || []
            });
        }
    }, [existingVendor]);

    // Fetch available taxes with fallback
    useEffect(() => {
        if (currentOrganization?.organization_id) {
            fetchAvailableTaxes();
            fetchTdsTaxes();
        }
    }, [currentOrganization]);

    const fetchAvailableTaxes = async () => {
        if (!currentOrganization?.organization_id) {
            console.log('❌ No organization ID available for fetching taxes');
            setAvailableTaxes(defaultTaxOptions);
            return;
        }

        setTaxesLoading(true);
        try {
            console.log(`🔍 Attempting to fetch taxes for organization: ${currentOrganization.organization_id}`);

            // Try the main taxes endpoint first
            let response = await fetch(`${API_BASE_URL}/taxes?company_id=${currentOrganization.organization_id}`, {
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                console.log('❌ Main taxes endpoint failed, trying Zoho taxes endpoint');
                // Fallback to zoho taxes endpoint
                response = await fetch(`${API_BASE_URL}/zoho/taxes?company_id=${currentOrganization.organization_id}`, {
                    headers: getAuthHeaders()
                });
            }

            if (response.ok) {
                const data = await response.json();
                if (data.success && Array.isArray(data.taxes)) {
                    setAvailableTaxes(data.taxes);
                    console.log(`✅ Fetched ${data.taxes.length} taxes`);
                } else {
                    console.log('⚠️ API response not in expected format, using defaults');
                    setAvailableTaxes(defaultTaxOptions);
                }
            } else {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
        } catch (error) {
            console.error('❌ Error fetching taxes, using defaults:', error);
            setAvailableTaxes(defaultTaxOptions);
        } finally {
            setTaxesLoading(false);
        }
    };

    const fetchTdsTaxes = async () => {
        if (!currentOrganization?.organization_id) {
            console.log('❌ No organization ID available for fetching TDS taxes');
            setTdsTaxes(defaultTdsOptions);
            return;
        }

        try {
            console.log(`🔍 Attempting to fetch TDS taxes for organization: ${currentOrganization.organization_id}`);

            // Try the main TDS taxes endpoint first
            let response = await fetch(`${API_BASE_URL}/tds-taxes?company_id=${currentOrganization.organization_id}`, {
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                console.log('❌ Main TDS taxes endpoint failed, trying Zoho TDS taxes endpoint');
                // Fallback to zoho TDS taxes endpoint
                response = await fetch(`${API_BASE_URL}/zoho/tds-taxes?company_id=${currentOrganization.organization_id}`, {
                    headers: getAuthHeaders()
                });
            }

            if (response.ok) {
                const data = await response.json();
                if (data.success && Array.isArray(data.taxes)) {
                    setTdsTaxes(data.taxes);
                    console.log(`✅ Fetched ${data.taxes.length} TDS taxes`);
                } else {
                    console.log('⚠️ TDS API response not in expected format, using defaults');
                    setTdsTaxes(defaultTdsOptions);
                }
            } else {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
        } catch (error) {
            console.error('❌ Error fetching TDS taxes, using defaults:', error);
            setTdsTaxes(defaultTdsOptions);
        }
    };

    // GST Validation
    const validateGSTNumber = (gstNo) => {
        if (!gstNo) {
            setGstValidation({ isValid: false, message: '' });
            return false;
        }

        // GST Number Format: 22AAAAA0000A1Z5
        const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

        if (!gstRegex.test(gstNo)) {
            setGstValidation({
                isValid: false,
                message: 'Invalid GST format. Expected: 22AAAAA0000A1Z5'
            });
            return false;
        }

        // Extract state code
        const stateCode = gstNo.substring(0, 2);
        const stateMapping = {
            '01': 'Jammu and Kashmir', '02': 'Himachal Pradesh', '03': 'Punjab',
            '04': 'Chandigarh', '05': 'Uttarakhand', '06': 'Haryana',
            '07': 'Delhi', '08': 'Rajasthan', '09': 'Uttar Pradesh',
            '10': 'Bihar', '11': 'Sikkim', '12': 'Arunachal Pradesh',
            '13': 'Nagaland', '14': 'Manipur', '15': 'Mizoram',
            '16': 'Tripura', '17': 'Meghalaya', '18': 'Assam',
            '19': 'West Bengal', '20': 'Jharkhand', '21': 'Odisha',
            '22': 'Chhattisgarh', '23': 'Madhya Pradesh', '24': 'Gujarat',
            '26': 'Dadra and Nagar Haveli and Daman and Diu', '27': 'Maharashtra',
            '29': 'Karnataka', '30': 'Goa', '31': 'Lakshadweep',
            '32': 'Kerala', '33': 'Tamil Nadu', '34': 'Puducherry',
            '35': 'Andaman and Nicobar Islands', '36': 'Telangana', '37': 'Andhra Pradesh'
        };

        const state = stateMapping[stateCode];
        if (state) {
            setFormData(prev => ({ ...prev, place_of_supply: state }));
            setGstValidation({
                isValid: true,
                message: `Valid GST - State: ${state}`
            });
            return true;
        }

        setGstValidation({
            isValid: false,
            message: 'Invalid state code in GST number'
        });
        return false;
    };

    // Handle GST number change
    const handleGSTChange = (value) => {
        const upperValue = value.toUpperCase();
        setFormData(prev => ({ ...prev, gst_no: upperValue }));
        validateGSTNumber(upperValue);
    };

    // Search GST details from government API (mock)
    const searchGSTDetails = async () => {
        if (!formData.gst_no || !gstValidation.isValid) {
            alert('Please enter a valid GST number');
            return;
        }

        setSearchingGST(true);
        try {
            // In real implementation, this would call government GST API
            // For now, we'll simulate with a timeout
            await new Promise(resolve => setTimeout(resolve, 1500));

            // Mock data - in real app, this would come from API
            const mockGSTData = {
                legalName: 'ABC Enterprises Pvt Ltd',
                tradeName: 'ABC Enterprises',
                address: '123, Main Street, Chennai',
                state: formData.place_of_supply,
                pincode: '600001'
            };

            setFormData(prev => ({
                ...prev,
                company_name: mockGSTData.legalName,
                billing_address: mockGSTData.address,
                billing_zip: mockGSTData.pincode,
                billing_state: mockGSTData.state
            }));

            alert('GST details fetched successfully!');
        } catch (error) {
            alert('Failed to fetch GST details. Please enter manually.');
        } finally {
            setSearchingGST(false);
        }
    };

    // Handle same as shipping address
    useEffect(() => {
        if (sameAsShipping) {
            setFormData(prev => ({
                ...prev,
                shipping_attention: prev.billing_attention,
                shipping_address: prev.billing_address,
                shipping_street2: prev.billing_street2,
                shipping_city: prev.billing_city,
                shipping_state: prev.billing_state,
                shipping_zip: prev.billing_zip,
                shipping_country: prev.billing_country,
                shipping_fax: prev.billing_fax
            }));
        }
    }, [sameAsShipping, formData.billing_attention, formData.billing_address,
        formData.billing_street2, formData.billing_city, formData.billing_state,
        formData.billing_zip, formData.billing_country, formData.billing_fax]);

    // Add contact person
    const addContactPerson = () => {
        setFormData(prev => ({
            ...prev,
            contact_persons: [
                ...prev.contact_persons,
                {
                    salutation: '',
                    first_name: '',
                    last_name: '',
                    email: '',
                    phone: '',
                    mobile: '',
                    designation: '',
                    department: '',
                    is_primary_contact: prev.contact_persons.length === 0
                }
            ]
        }));
    };

    // Remove contact person
    const removeContactPerson = (index) => {
        setFormData(prev => ({
            ...prev,
            contact_persons: prev.contact_persons.filter((_, i) => i !== index)
        }));
    };

    // Update contact person
    const updateContactPerson = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            contact_persons: prev.contact_persons.map((person, i) =>
                i === index ? { ...person, [field]: value } : person
            )
        }));
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        // Required fields
        if (!formData.contact_name.trim()) {
            newErrors.contact_name = 'Vendor name is required';
        }

        // Email validation
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Invalid email format';
        }

        // Phone validation
        if (formData.phone && !/^[0-9+\-\s()]+$/.test(formData.phone)) {
            newErrors.phone = 'Invalid phone format';
        }

        // GST validation for Indian vendors
        if (formData.gst_treatment === 'business_gst' && !formData.gst_no) {
            newErrors.gst_no = 'GST number is required for registered businesses';
        }

        // Place of supply required for GST registered
        if (formData.gst_no && !formData.place_of_supply) {
            newErrors.place_of_supply = 'Place of supply is required for GST registered vendors';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async () => {
        if (!validateForm()) {
            alert('Please fix the errors before submitting');
            return;
        }

        if (!currentOrganization?.organization_id) {
            alert('❌ No organization selected. Please refresh the page and try again.');
            return;
        }

        setLoading(true);
        try {
            // Prepare data for Zoho Books API
            const vendorData = {
                contact_name: formData.contact_name,
                company_name: formData.company_name || '',
                contact_type: 'vendor',

                // Contact details - ensure these are included
                email: formData.email || '',
                phone: formData.phone || '',
                mobile: formData.mobile || '',
                website: formData.website || '',

                // GST details
                gst_no: formData.gst_no || '',
                gst_treatment: formData.gst_treatment,
                place_of_supply: formData.place_of_supply || '',

                // Tax details
                tax_id: formData.tax_id || '',
                is_tds_applicable: formData.is_tds_applicable || false,
                tds_tax_id: formData.tds_tax_id || '',

                // Payment terms
                payment_terms: formData.payment_terms,
                payment_terms_label: formData.payment_terms_label,
                currency_code: formData.currency_code,

                // Billing address
                billing_address: {
                    attention: formData.billing_attention || '',
                    address: formData.billing_address || '',
                    street2: formData.billing_street2 || '',
                    city: formData.billing_city || '',
                    state: formData.billing_state || '',
                    zip: formData.billing_zip || '',
                    country: formData.billing_country || '',
                    fax: formData.billing_fax || ''
                },

                // Shipping address
                shipping_address: {
                    attention: formData.shipping_attention || '',
                    address: formData.shipping_address || '',
                    street2: formData.shipping_street2 || '',
                    city: formData.shipping_city || '',
                    state: formData.shipping_state || '',
                    zip: formData.shipping_zip || '',
                    country: formData.shipping_country || '',
                    fax: formData.shipping_fax || ''
                },

                // Bank details
                bank_details: [{
                    account_holder_name: formData.bank_account_holder_name || '',
                    account_number: formData.bank_account_number || '',
                    bank_name: formData.bank_name || '',
                    branch: formData.bank_branch || '',
                    ifsc_code: formData.bank_ifsc_code || '',
                    account_type: formData.bank_account_type || 'savings'
                }],

                // Contact persons
                contact_persons: Array.isArray(formData.contact_persons) ?
                    formData.contact_persons : [],

                // Additional details
                notes: formData.notes || '',
                custom_fields: Array.isArray(formData.custom_fields) ?
                    formData.custom_fields : []
            };

            console.log('🔍 Submitting vendor data for organization:', currentOrganization.organization_id);
            console.log('📋 Vendor data:', vendorData);

            const endpoint = existingVendor
                ? `${API_BASE_URL}/zoho/vendors/${existingVendor.contact_id}?company_id=${currentOrganization.organization_id}`
                : `${API_BASE_URL}/zoho/vendors_alone?company_id=${currentOrganization.organization_id}`;

            const method = existingVendor ? 'PUT' : 'POST';

            const response = await fetch(endpoint, {
                method: method,
                headers: getAuthHeaders(),
                body: JSON.stringify(vendorData)
            });

            const data = await response.json();

            if (data.success) {
                alert(`Vendor ${existingVendor ? 'updated' : 'created'} successfully!`);
                if (onSuccess) {
                    onSuccess(data.vendor);
                }
                onClose();
            } else {
                alert(`Failed to ${existingVendor ? 'update' : 'create'} vendor: ${data.message}`);
            }
        } catch (error) {
            console.error('Error submitting vendor:', error);
            alert(`Error: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Tab navigation
    const tabs = [
        { id: 'basic', label: 'Basic Info', icon: Building },
        { id: 'gst', label: 'GST & Tax', icon: Calculator },
        { id: 'address', label: 'Addresses', icon: MapPin },
        { id: 'persons', label: 'Contact Persons', icon: User },
        { id: 'additional', label: 'Additional', icon: FileText }
    ];

    // Render Basic Info Tab
    const renderBasicInfoTab = () => (
        <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Vendor Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        value={formData.contact_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                        className={`w-full border rounded-md px-3 py-2 ${errors.contact_name ? 'border-red-500' : ''}`}
                        placeholder="Enter vendor name"
                    />
                    {errors.contact_name && (
                        <p className="text-red-500 text-xs mt-1">{errors.contact_name}</p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Company Name
                    </label>
                    <input
                        type="text"
                        value={formData.company_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                        className="w-full border rounded-md px-3 py-2"
                        placeholder="Enter company name"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                    </label>
                    <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        className={`w-full border rounded-md px-3 py-2 ${errors.email ? 'border-red-500' : ''}`}
                        placeholder="vendor@example.com"
                    />
                    {errors.email && (
                        <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone
                    </label>
                    <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        className={`w-full border rounded-md px-3 py-2 ${errors.phone ? 'border-red-500' : ''}`}
                        placeholder="+91 1234567890"
                    />
                    {errors.phone && (
                        <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Mobile
                    </label>
                    <input
                        type="tel"
                        value={formData.mobile}
                        onChange={(e) => setFormData(prev => ({ ...prev, mobile: e.target.value }))}
                        className="w-full border rounded-md px-3 py-2"
                        placeholder="+91 9876543210"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Website
                    </label>
                    <input
                        type="url"
                        value={formData.website}
                        onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                        className="w-full border rounded-md px-3 py-2"
                        placeholder="https://vendor-website.com"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Currency
                    </label>
                    <select
                        value={formData.currency_code}
                        onChange={(e) => setFormData(prev => ({ ...prev, currency_code: e.target.value }))}
                        className="w-full border rounded-md px-3 py-2"
                    >
                        <option value="INR">INR - Indian Rupee</option>
                        <option value="USD">USD - US Dollar</option>
                        <option value="EUR">EUR - Euro</option>
                        <option value="GBP">GBP - British Pound</option>
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Terms
                    </label>
                    <select
                        value={formData.payment_terms}
                        onChange={(e) => {
                            const selectedOption = paymentTermsOptions.find(opt => opt.value === parseInt(e.target.value));
                            setFormData(prev => ({
                                ...prev,
                                payment_terms: parseInt(e.target.value),
                                payment_terms_label: selectedOption?.label || ''
                            }));
                        }}
                        className="w-full border rounded-md px-3 py-2"
                    >
                        {paymentTermsOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );

    // Render GST & Tax Tab
    const renderGSTTaxTab = () => (
        <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        GST Treatment
                    </label>
                    <select
                        value={formData.gst_treatment}
                        onChange={(e) => setFormData(prev => ({ ...prev, gst_treatment: e.target.value }))}
                        className="w-full border rounded-md px-3 py-2"
                    >
                        {gstTreatmentOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        GST Number {formData.gst_treatment === 'business_gst' && <span className="text-red-500">*</span>}
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={formData.gst_no}
                            onChange={(e) => handleGSTChange(e.target.value)}
                            className={`flex-1 border rounded-md px-3 py-2 ${errors.gst_no ? 'border-red-500' : ''}`}
                            placeholder="22AAAAA0000A1Z5"
                            maxLength="15"
                        />
                        <button
                            onClick={searchGSTDetails}
                            disabled={!gstValidation.isValid || searchingGST}
                            className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {searchingGST ? (
                                <Loader className="w-4 h-4 animate-spin" />
                            ) : (
                                <Search className="w-4 h-4" />
                            )}
                        </button>
                    </div>
                    {gstValidation.message && (
                        <p className={`text-xs mt-1 ${gstValidation.isValid ? 'text-green-600' : 'text-red-500'}`}>
                            {gstValidation.message}
                        </p>
                    )}
                    {errors.gst_no && (
                        <p className="text-red-500 text-xs mt-1">{errors.gst_no}</p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Place of Supply {formData.gst_no && <span className="text-red-500">*</span>}
                    </label>
                    <select
                        value={formData.place_of_supply}
                        onChange={(e) => setFormData(prev => ({ ...prev, place_of_supply: e.target.value }))}
                        className={`w-full border rounded-md px-3 py-2 ${errors.place_of_supply ? 'border-red-500' : ''}`}
                    >
                        <option value="">Select State</option>
                        {indianStates.map(state => (
                            <option key={state} value={state}>{state}</option>
                        ))}
                    </select>
                    {errors.place_of_supply && (
                        <p className="text-red-500 text-xs mt-1">{errors.place_of_supply}</p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Default Tax
                        {taxesLoading && (
                            <span className="text-xs text-blue-600 ml-2">
                                <Loader className="w-3 h-3 inline animate-spin mr-1" />
                                Loading...
                            </span>
                        )}
                    </label>
                    <select
                        value={formData.tax_id}
                        onChange={(e) => {
                            const selectedTax = availableTaxes.find(tax => tax.tax_id === e.target.value);
                            setFormData(prev => ({
                                ...prev,
                                tax_id: e.target.value,
                                tax_name: selectedTax?.tax_name || '',
                                tax_percentage: selectedTax?.tax_percentage || ''
                            }));
                        }}
                        className="w-full border rounded-md px-3 py-2"
                        disabled={taxesLoading}
                    >
                        <option value="">No Tax</option>
                        {availableTaxes.map(tax => (
                            <option key={tax.tax_id} value={tax.tax_id}>
                                {tax.tax_name} ({tax.tax_percentage}%)
                            </option>
                        ))}
                    </select>
                    {availableTaxes.length === 0 && !taxesLoading && (
                        <p className="text-xs text-yellow-600 mt-1">
                            Using default tax options - API not available
                        </p>
                    )}
                </div>
            </div>

            <div className="border-t pt-4">
                <label className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        checked={formData.is_tds_applicable}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_tds_applicable: e.target.checked }))}
                        className="rounded"
                    />
                    <span className="text-sm font-medium">TDS Applicable</span>
                </label>

                {formData.is_tds_applicable && (
                    <div className="mt-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            TDS Tax
                            {taxesLoading && (
                                <span className="text-xs text-blue-600 ml-2">
                                    <Loader className="w-3 h-3 inline animate-spin mr-1" />
                                    Loading...
                                </span>
                            )}
                        </label>
                        <select
                            value={formData.tds_tax_id}
                            onChange={(e) => setFormData(prev => ({ ...prev, tds_tax_id: e.target.value }))}
                            className="w-full border rounded-md px-3 py-2"
                            disabled={taxesLoading}
                        >
                            <option value="">Select TDS Tax</option>
                            {tdsTaxes.map(tax => (
                                <option key={tax.tax_id} value={tax.tax_id}>
                                    {tax.tax_name} ({tax.tax_percentage}%)
                                </option>
                            ))}
                        </select>
                        {tdsTaxes.length === 0 && !taxesLoading && (
                            <p className="text-xs text-yellow-600 mt-1">
                                Using default TDS options - API not available
                            </p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );

    // Render Address Tab
    const renderAddressTab = () => (
        <div className="space-y-6">
            {/* Billing Address */}
            <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Billing Address</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Attention
                        </label>
                        <input
                            type="text"
                            value={formData.billing_attention}
                            onChange={(e) => setFormData(prev => ({ ...prev, billing_attention: e.target.value }))}
                            className="w-full border rounded-md px-3 py-2"
                            placeholder="Contact person name"
                        />
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Address Line 1
                        </label>
                        <input
                            type="text"
                            value={formData.billing_address}
                            onChange={(e) => setFormData(prev => ({ ...prev, billing_address: e.target.value }))}
                            className="w-full border rounded-md px-3 py-2"
                            placeholder="Street address"
                        />
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Address Line 2
                        </label>
                        <input
                            type="text"
                            value={formData.billing_street2}
                            onChange={(e) => setFormData(prev => ({ ...prev, billing_street2: e.target.value }))}
                            className="w-full border rounded-md px-3 py-2"
                            placeholder="Apartment, suite, etc."
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            City
                        </label>
                        <input
                            type="text"
                            value={formData.billing_city}
                            onChange={(e) => setFormData(prev => ({ ...prev, billing_city: e.target.value }))}
                            className="w-full border rounded-md px-3 py-2"
                            placeholder="City"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            State
                        </label>
                        <select
                            value={formData.billing_state}
                            onChange={(e) => setFormData(prev => ({ ...prev, billing_state: e.target.value }))}
                            className="w-full border rounded-md px-3 py-2"
                        >
                            <option value="">Select State</option>
                            {indianStates.map(state => (
                                <option key={state} value={state}>{state}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            ZIP/Postal Code
                        </label>
                        <input
                            type="text"
                            value={formData.billing_zip}
                            onChange={(e) => setFormData(prev => ({ ...prev, billing_zip: e.target.value }))}
                            className="w-full border rounded-md px-3 py-2"
                            placeholder="600001"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Country
                        </label>
                        <input
                            type="text"
                            value={formData.billing_country}
                            onChange={(e) => setFormData(prev => ({ ...prev, billing_country: e.target.value }))}
                            className="w-full border rounded-md px-3 py-2"
                            placeholder="India"
                        />
                    </div>
                </div>
            </div>

            {/* Shipping Address */}
            <div>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Shipping Address</h3>
                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={sameAsShipping}
                            onChange={(e) => setSameAsShipping(e.target.checked)}
                            className="rounded"
                        />
                        <span className="text-sm">Same as billing address</span>
                    </label>
                </div>

                <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${sameAsShipping ? 'opacity-50 pointer-events-none' : ''}`}>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Attention
                        </label>
                        <input
                            type="text"
                            value={formData.shipping_attention}
                            onChange={(e) => setFormData(prev => ({ ...prev, shipping_attention: e.target.value }))}
                            className="w-full border rounded-md px-3 py-2"
                            placeholder="Contact person name"
                            disabled={sameAsShipping}
                        />
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Address Line 1
                        </label>
                        <input
                            type="text"
                            value={formData.shipping_address}
                            onChange={(e) => setFormData(prev => ({ ...prev, shipping_address: e.target.value }))}
                            className="w-full border rounded-md px-3 py-2"
                            placeholder="Street address"
                            disabled={sameAsShipping}
                        />
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Address Line 2
                        </label>
                        <input
                            type="text"
                            value={formData.shipping_street2}
                            onChange={(e) => setFormData(prev => ({ ...prev, shipping_street2: e.target.value }))}
                            className="w-full border rounded-md px-3 py-2"
                            placeholder="Apartment, suite, etc."
                            disabled={sameAsShipping}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            City
                        </label>
                        <input
                            type="text"
                            value={formData.shipping_city}
                            onChange={(e) => setFormData(prev => ({ ...prev, shipping_city: e.target.value }))}
                            className="w-full border rounded-md px-3 py-2"
                            placeholder="City"
                            disabled={sameAsShipping}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            State
                        </label>
                        <select
                            value={formData.shipping_state}
                            onChange={(e) => setFormData(prev => ({ ...prev, shipping_state: e.target.value }))}
                            className="w-full border rounded-md px-3 py-2"
                            disabled={sameAsShipping}
                        >
                            <option value="">Select State</option>
                            {indianStates.map(state => (
                                <option key={state} value={state}>{state}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            ZIP/Postal Code
                        </label>
                        <input
                            type="text"
                            value={formData.shipping_zip}
                            onChange={(e) => setFormData(prev => ({ ...prev, shipping_zip: e.target.value }))}
                            className="w-full border rounded-md px-3 py-2"
                            placeholder="600001"
                            disabled={sameAsShipping}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Country
                        </label>
                        <input
                            type="text"
                            value={formData.shipping_country}
                            onChange={(e) => setFormData(prev => ({ ...prev, shipping_country: e.target.value }))}
                            className="w-full border rounded-md px-3 py-2"
                            placeholder="India"
                            disabled={sameAsShipping}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    // Render Contact Persons Tab
    const renderContactPersonsTab = () => (
        <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Contact Persons</h3>
                <button
                    onClick={addContactPerson}
                    className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                    <Plus className="w-4 h-4" />
                    Add Contact Person
                </button>
            </div>

            {formData.contact_persons.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <User className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500">No contact persons added yet</p>
                    <p className="text-sm text-gray-400 mt-1">Click "Add Contact Person" to add one</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {formData.contact_persons.map((person, index) => (
                        <div key={index} className="border rounded-lg p-4 bg-gray-50">
                            <div className="flex justify-between items-start mb-3">
                                <h4 className="font-medium text-gray-900">
                                    Contact Person {index + 1}
                                    {person.is_primary_contact && (
                                        <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Primary</span>
                                    )}
                                </h4>
                                <button
                                    onClick={() => removeContactPerson(index)}
                                    className="text-red-600 hover:text-red-800"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Salutation
                                    </label>
                                    <select
                                        value={person.salutation}
                                        onChange={(e) => updateContactPerson(index, 'salutation', e.target.value)}
                                        className="w-full border rounded-md px-2 py-1 text-sm"
                                    >
                                        <option value="">Select</option>
                                        <option value="Mr.">Mr.</option>
                                        <option value="Ms.">Ms.</option>
                                        <option value="Mrs.">Mrs.</option>
                                        <option value="Dr.">Dr.</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        First Name
                                    </label>
                                    <input
                                        type="text"
                                        value={person.first_name}
                                        onChange={(e) => updateContactPerson(index, 'first_name', e.target.value)}
                                        className="w-full border rounded-md px-2 py-1 text-sm"
                                        placeholder="First name"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Last Name
                                    </label>
                                    <input
                                        type="text"
                                        value={person.last_name}
                                        onChange={(e) => updateContactPerson(index, 'last_name', e.target.value)}
                                        className="w-full border rounded-md px-2 py-1 text-sm"
                                        placeholder="Last name"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        value={person.email}
                                        onChange={(e) => updateContactPerson(index, 'email', e.target.value)}
                                        className="w-full border rounded-md px-2 py-1 text-sm"
                                        placeholder="email@example.com"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Phone
                                    </label>
                                    <input
                                        type="tel"
                                        value={person.phone}
                                        onChange={(e) => updateContactPerson(index, 'phone', e.target.value)}
                                        className="w-full border rounded-md px-2 py-1 text-sm"
                                        placeholder="+91 1234567890"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Mobile
                                    </label>
                                    <input
                                        type="tel"
                                        value={person.mobile}
                                        onChange={(e) => updateContactPerson(index, 'mobile', e.target.value)}
                                        className="w-full border rounded-md px-2 py-1 text-sm"
                                        placeholder="+91 9876543210"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Designation
                                    </label>
                                    <input
                                        type="text"
                                        value={person.designation}
                                        onChange={(e) => updateContactPerson(index, 'designation', e.target.value)}
                                        className="w-full border rounded-md px-2 py-1 text-sm"
                                        placeholder="Manager, Director, etc."
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Department
                                    </label>
                                    <input
                                        type="text"
                                        value={person.department}
                                        onChange={(e) => updateContactPerson(index, 'department', e.target.value)}
                                        className="w-full border rounded-md px-2 py-1 text-sm"
                                        placeholder="Sales, Finance, etc."
                                    />
                                </div>

                                <div className="flex items-center">
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={person.is_primary_contact}
                                            onChange={(e) => {
                                                // Set all others to false, then set this one
                                                const updatedPersons = formData.contact_persons.map((p, i) => ({
                                                    ...p,
                                                    is_primary_contact: i === index ? e.target.checked : false
                                                }));
                                                setFormData(prev => ({ ...prev, contact_persons: updatedPersons }));
                                            }}
                                            className="rounded"
                                        />
                                        <span className="text-sm">Primary Contact</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    // Render Additional Tab
    const renderAdditionalTab = () => (
        <div className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                </label>
                <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full border rounded-md px-3 py-2"
                    rows="4"
                    placeholder="Add any additional notes about this vendor..."
                />
                <p className="text-xs text-gray-500 mt-1">These notes are for internal reference only</p>
            </div>

            {/* Organization Info Display */}
            {currentOrganization && (
                <div className="bg-blue-50 p-3 rounded border">
                    <div className="text-sm font-medium text-blue-700 mb-2">Organization Context</div>
                    <div className="space-y-1 text-xs text-blue-600">
                        <div><strong>Organization:</strong> {currentOrganization.name}</div>
                        <div><strong>ID:</strong> {currentOrganization.organization_id}</div>
                        <div><strong>Currency:</strong> {currentOrganization.currency_code}</div>
                    </div>
                </div>
            )}

            {/* API Status Display */}
            <div className="bg-gray-50 p-3 rounded border">
                <div className="text-sm font-medium text-gray-700 mb-2">API Status</div>
                <div className="space-y-1 text-xs">
                    <div className="flex items-center gap-2">
                        {availableTaxes.length > 0 ? (
                            <CheckCircle className="w-3 h-3 text-green-500" />
                        ) : (
                            <AlertTriangle className="w-3 h-3 text-yellow-500" />
                        )}
                        <span>Tax Options: {availableTaxes.length} available</span>
                    </div>
                    <div className="flex items-center gap-2">
                        {tdsTaxes.length > 0 ? (
                            <CheckCircle className="w-3 h-3 text-green-500" />
                        ) : (
                            <AlertTriangle className="w-3 h-3 text-yellow-500" />
                        )}
                        <span>TDS Options: {tdsTaxes.length} available</span>
                    </div>
                </div>
                {(availableTaxes.length === 0 || tdsTaxes.length === 0) && (
                    <p className="text-xs text-yellow-600 mt-2">
                        Using fallback options - some API endpoints may not be available
                    </p>
                )}
            </div>
        </div>
    );

    // Render tab content based on active tab
    const renderTabContent = () => {
        switch (activeTab) {
            case 'basic':
                return renderBasicInfoTab();
            case 'gst':
                return renderGSTTaxTab();
            case 'address':
                return renderAddressTab();
            case 'persons':
                return renderContactPersonsTab();
            case 'additional':
                return renderAdditionalTab();
            default:
                return renderBasicInfoTab();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b flex justify-between items-center">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">
                            {existingVendor ? 'Edit Vendor' : 'Create New Vendor'}
                        </h2>
                        {currentOrganization && (
                            <p className="text-sm text-gray-600 mt-1">
                                Organization: <span className="font-medium text-blue-600">{currentOrganization.name}</span>
                            </p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Organization Warning */}
                {!currentOrganization?.organization_id && (
                    <div className="px-6 py-3 bg-red-50 border-b border-red-200">
                        <div className="flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-red-600" />
                            <div className="text-sm text-red-700">
                                <strong>No organization selected.</strong> Please refresh the page and try again.
                            </div>
                        </div>
                    </div>
                )}

                {/* Tab Navigation */}
                <div className="border-b px-6">
                    <div className="flex space-x-8">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`py-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${activeTab === tab.id
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                        }`}
                                >
                                    <Icon className="w-4 h-4" />
                                    {tab.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Tab Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {renderTabContent()}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t flex justify-between items-center bg-gray-50">
                    <div className="text-sm text-gray-600">
                        {Object.keys(errors).length > 0 && (
                            <div className="flex items-center gap-2 text-red-600">
                                <AlertCircle className="w-4 h-4" />
                                Please fix {Object.keys(errors).length} error(s) before submitting
                            </div>
                        )}
                        {!currentOrganization?.organization_id && (
                            <div className="flex items-center gap-2 text-red-600">
                                <AlertCircle className="w-4 h-4" />
                                Organization context required
                            </div>
                        )}
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={loading || !currentOrganization?.organization_id}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <Loader className="w-4 h-4 animate-spin" />
                                    {existingVendor ? 'Updating...' : 'Creating...'}
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    {existingVendor ? 'Update Vendor' : 'Create Vendor'}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Vendor Management Component
const VendorManagement = ({ isEmbeddedForm = false, onVendorCreated = null, currentOrganization }) => {
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingVendor, setEditingVendor] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [selectedVendors, setSelectedVendors] = useState([]);

    useEffect(() => {
        if (currentOrganization?.organization_id) {
            fetchVendors();
        }
    }, [currentOrganization]);

    const fetchVendors = async () => {
        if (!currentOrganization?.organization_id) {
            console.log('❌ No organization ID available, skipping vendors fetch');
            return;
        }

        setLoading(true);
        try {
            console.log(`🔍 Fetching vendors for organization: ${currentOrganization.organization_id}`);
            const response = await fetch(`${API_BASE_URL}/vendors?company_id=${currentOrganization.organization_id}`, {
                headers: getAuthHeaders()
            });
            const data = await response.json();
            if (data.success) {
                setVendors(data.vendors || []);
                console.log(`✅ Fetched ${data.vendors?.length || 0} vendors`);
            } else {
                console.error('❌ Failed to fetch vendors:', data.message);
                alert('Failed to fetch vendors: ' + data.message);
            }
        } catch (error) {
            console.error('Error fetching vendors:', error);
            alert('Failed to fetch vendors');
        } finally {
            setLoading(false);
        }
    };

    // If embedded form mode is on, just show the creation form
    if (isEmbeddedForm) {
        return (
            <VendorCreationModal
                isOpen={true}
                onClose={() => onVendorCreated && onVendorCreated(null)}
                onSuccess={(vendor) => {
                    if (onVendorCreated) {
                        onVendorCreated(vendor);
                    }
                }}
                existingVendor={null}
                currentOrganization={currentOrganization}
            />
        );
    }

    const handleCreateVendor = () => {
        setEditingVendor(null);
        setShowCreateModal(true);
    };

    const handleEditVendor = (vendor) => {
        setEditingVendor(vendor);
        setShowCreateModal(true);
    };

    const handleDeleteVendor = async (vendorId) => {
        if (!confirm('Are you sure you want to delete this vendor?')) return;

        try {
            const response = await fetch(`${API_BASE_URL}/zoho/vendors/${vendorId}?company_id=${currentOrganization.organization_id}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });
            const data = await response.json();

            if (data.success) {
                alert('Vendor deleted successfully');
                fetchVendors();
            } else {
                alert('Failed to delete vendor: ' + data.message);
            }
        } catch (error) {
            console.error('Error deleting vendor:', error);
            alert('Error deleting vendor');
        }
    };

    const handleVendorSuccess = (vendor) => {
        fetchVendors();
    };

    // Filter vendors based on search and filter type
    const filteredVendors = vendors.filter(vendor => {
        const matchesSearch = vendor.contact_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            vendor.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            vendor.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            vendor.gst_no?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesFilter = filterType === 'all' ||
            (filterType === 'gst' && vendor.gst_no) ||
            (filterType === 'tds' && vendor.is_tds_applicable) ||
            (filterType === 'active' && vendor.status === 'active') ||
            (filterType === 'inactive' && vendor.status === 'inactive');

        return matchesSearch && matchesFilter;
    });

    // Show organization warning if no organization
    if (!currentOrganization?.organization_id) {
        return (
            <div className="max-w-7xl mx-auto p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                    <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-red-900 mb-2">No Organization Selected</h2>
                    <p className="text-red-700">Please refresh the page and try again.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto p-6 space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg border p-6">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Vendor Management</h1>
                        <p className="text-gray-600 mt-2">Manage your vendors with GST and TDS compliance</p>
                        <p className="text-gray-600 mt-1">
                            Organization: <span className="font-medium text-blue-600">{currentOrganization.name}</span>
                        </p>
                    </div>
                    <button
                        onClick={handleCreateVendor}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        <Plus className="w-4 h-4" />
                        Create Vendor
                    </button>
                </div>

                {/* Search and Filters */}
                <div className="flex flex-wrap gap-4">
                    <div className="flex-1 min-w-[300px]">
                        <div className="relative">
                            <Search className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search vendors by name, email, GST number..."
                                className="w-full pl-10 pr-4 py-2 border rounded-md"
                            />
                        </div>
                    </div>
                    <div className="flex gap-2">
                        {['all', 'gst', 'tds', 'active', 'inactive'].map(type => (
                            <button
                                key={type}
                                onClick={() => setFilterType(type)}
                                className={`px-4 py-2 rounded-md text-sm font-medium ${filterType === type
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                            >
                                {type.charAt(0).toUpperCase() + type.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Vendors Table */}
            <div className="bg-white rounded-lg border">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b">
                            <tr>
                                <th className="text-left p-4">
                                    <input
                                        type="checkbox"
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedVendors(filteredVendors.map(v => v.contact_id));
                                            } else {
                                                setSelectedVendors([]);
                                            }
                                        }}
                                        checked={selectedVendors.length === filteredVendors.length && filteredVendors.length > 0}
                                        className="rounded"
                                    />
                                </th>
                                <th className="text-left p-4">Vendor Details</th>
                                <th className="text-left p-4">Contact Info</th>
                                <th className="text-left p-4">GST Details</th>
                                <th className="text-left p-4">Payment Terms</th>
                                <th className="text-left p-4">Status</th>
                                <th className="text-left p-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="7" className="text-center py-8">
                                        <Loader className="w-8 h-8 animate-spin mx-auto text-gray-400" />
                                        <p className="text-gray-500 mt-2">Loading vendors...</p>
                                    </td>
                                </tr>
                            ) : filteredVendors.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="text-center py-8">
                                        <Building className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                                        <p className="text-gray-500">No vendors found</p>
                                    </td>
                                </tr>
                            ) : (
                                filteredVendors.map((vendor) => (
                                    <tr key={vendor.contact_id} className="border-b hover:bg-gray-50">
                                        <td className="p-4">
                                            <input
                                                type="checkbox"
                                                checked={selectedVendors.includes(vendor.contact_id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedVendors([...selectedVendors, vendor.contact_id]);
                                                    } else {
                                                        setSelectedVendors(selectedVendors.filter(id => id !== vendor.contact_id));
                                                    }
                                                }}
                                                className="rounded"
                                            />
                                        </td>
                                        <td className="p-4">
                                            <div className="font-medium text-gray-900">{vendor.contact_name}</div>
                                            {vendor.company_name && (
                                                <div className="text-sm text-gray-600">{vendor.company_name}</div>
                                            )}
                                            <div className="text-xs text-gray-500">ID: {vendor.contact_id}</div>
                                        </td>
                                        <td className="p-4">
                                            {vendor.email && (
                                                <div className="flex items-center gap-1 text-sm text-gray-600">
                                                    <Mail className="w-3 h-3" />
                                                    {vendor.email}
                                                </div>
                                            )}
                                            {vendor.phone && (
                                                <div className="flex items-center gap-1 text-sm text-gray-600 mt-1">
                                                    <Phone className="w-3 h-3" />
                                                    {vendor.phone}
                                                </div>
                                            )}
                                            {vendor.website && (
                                                <div className="flex items-center gap-1 text-xs text-blue-600 mt-1">
                                                    <Globe className="w-3 h-3" />
                                                    <a href={vendor.website} target="_blank" rel="noopener noreferrer">
                                                        {vendor.website.replace(/^https?:\/\//, '')}
                                                    </a>
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            {vendor.gst_no ? (
                                                <div>
                                                    <div className="flex items-center gap-1">
                                                        <Hash className="w-3 h-3 text-green-600" />
                                                        <span className="font-medium text-green-700">{vendor.gst_no}</span>
                                                    </div>
                                                    <div className="text-xs text-gray-600 mt-1">
                                                        {vendor.gst_treatment && (
                                                            <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs">
                                                                {vendor.gst_treatment === 'business_gst' ? 'Registered Regular' :
                                                                    vendor.gst_treatment === 'business_none' ? 'Composition' :
                                                                        vendor.gst_treatment === 'overseas' ? 'Overseas' :
                                                                            vendor.gst_treatment === 'consumer' ? 'Consumer' :
                                                                                vendor.gst_treatment === 'unregistered' ? 'Unregistered' :
                                                                                    vendor.gst_treatment === 'special_economic_zone' ? 'SEZ' :
                                                                                        vendor.gst_treatment === 'deemed_export' ? 'Deemed Export' :
                                                                                            vendor.gst_treatment}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {vendor.place_of_supply && (
                                                        <div className="text-xs text-gray-600 mt-1">
                                                            <MapPin className="w-3 h-3 inline mr-1" />
                                                            {vendor.place_of_supply}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="text-gray-500 text-sm">No GST Info</div>
                                            )}
                                            {vendor.is_tds_applicable && (
                                                <div className="mt-2 text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded inline-flex items-center">
                                                    <Calculator className="w-3 h-3 mr-1" />
                                                    TDS Applicable
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            <div className="text-sm">
                                                {vendor.payment_terms_label || `Net ${vendor.payment_terms || 0} days`}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                Currency: {vendor.currency_code || 'INR'}
                                            </div>
                                            {vendor.tax_id && (
                                                <div className="text-xs text-gray-600 mt-1">
                                                    Default Tax: {vendor.tax_name || vendor.tax_id}
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${vendor.status === 'active'
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-gray-100 text-gray-800'
                                                }`}>
                                                {vendor.status || 'Active'}
                                            </span>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleEditVendor(vendor)}
                                                    className="p-1 text-blue-600 hover:text-blue-800"
                                                    title="Edit"
                                                >
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteVendor(vendor.contact_id)}
                                                    className="p-1 text-red-600 hover:text-red-800"
                                                    title="Delete"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Bulk Actions Panel */}
            {selectedVendors.length > 0 && (
                <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-white rounded-lg shadow-lg border p-4 flex items-center gap-4">
                    <div className="text-sm font-medium text-gray-700">
                        {selectedVendors.length} vendor{selectedVendors.length > 1 ? 's' : ''} selected
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => {
                                // Implement bulk mark as active
                                alert(`Mark ${selectedVendors.length} vendors as active`);
                            }}
                            className="px-3 py-1 bg-green-100 text-green-700 rounded-md text-sm hover:bg-green-200"
                        >
                            <Check className="w-4 h-4 inline mr-1" />
                            Mark Active
                        </button>
                        <button
                            onClick={() => {
                                // Implement bulk mark as inactive
                                alert(`Mark ${selectedVendors.length} vendors as inactive`);
                            }}
                            className="px-3 py-1 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200"
                        >
                            <X className="w-4 h-4 inline mr-1" />
                            Mark Inactive
                        </button>
                        <button
                            onClick={() => {
                                if (confirm(`Are you sure you want to delete ${selectedVendors.length} vendors?`)) {
                                    // Implement bulk delete
                                    alert(`Deleted ${selectedVendors.length} vendors`);
                                    setSelectedVendors([]);
                                }
                            }}
                            className="px-3 py-1 bg-red-100 text-red-700 rounded-md text-sm hover:bg-red-200"
                        >
                            <Trash2 className="w-4 h-4 inline mr-1" />
                            Delete
                        </button>
                        <button
                            onClick={() => setSelectedVendors([])}
                            className="px-3 py-1 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Vendor Creation/Edit Modal */}
            {showCreateModal && (
                <VendorCreationModal
                    isOpen={showCreateModal}
                    onClose={() => {
                        setShowCreateModal(false);
                        setEditingVendor(null);
                    }}
                    onSuccess={handleVendorSuccess}
                    existingVendor={editingVendor}
                    currentOrganization={currentOrganization}
                />
            )}
        </div>
    );
};

export default VendorManagement;